import { ChangeDetectionStrategy, Component, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { GalleryLayout1 } from '@components/gallery/components/gallery-layout-1/model';
import { PageTypeService } from '@services/page-type';
import { ResponsiveService } from '@services/responsive';
import { ImageComponent } from '@shared/components/image';
import { TitleComponent } from '@shared/components/title';
import { Title } from '@shared/components/title/model';
import { PageType } from '@shared/enums';
import { Image } from '@shared/models';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'app-gallery-layout-1',
  imports: [TitleComponent, RouterLink, ImageComponent],
  templateUrl: './gallery-layout-1.component.html',
  styleUrl: './gallery-layout-1.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GalleryLayout1Component {
  readonly data = input.required<GalleryLayout1>();
  items = signal<
    {
      image: Image;
      title: Title;
      url: string;
    }[]
  >(null!);
  subtitle = signal<{ previous: Title; next: Title }>(null!);
  breakpoints = inject(ResponsiveService).breakpoints;
  readonly #router = inject(Router);
  readonly #pageTypeService = inject(PageTypeService);

  constructor() {
    effect(() => {
      if (this.breakpoints()) {
        const data = this.data();
        if (data.items.length === 1) {
          data.items.push(data.items[0]!);
          data.items.splice(0, 1);
        }
        this.items.set(
          this.data().items.map((item) => ({
            image: item.image,
            url: item.url,
            title: {
              level: 'SPAN',
              text: item.title,
              color: 'BLACK',
              align: {
                mobile: 'LEFT',
                desktop: 'LEFT',
              },
              weight: this.breakpoints() === 'mobile' ? 400 : 600,
              size: {
                mobile: 'H3',
                desktop: 'H4',
                custom: {
                  mobile: '2.4rem',
                },
              },
            },
          }))
        );
      }
    });
    this.#pageTypeService
      .getPageType$()
      .pipe(
        takeUntilDestroyed(),
        tap((pageType) => {
          this.subtitle.set({
            previous: {
              level: 'SPAN',
              font: 'INTER',
              color: 'GREY_LIGHT',
              text: this.getText(pageType, 'précédent'),
              align: {
                mobile: 'LEFT',
                desktop: 'LEFT',
              },
              size: {
                mobile: 'SPAN',
                desktop: 'SPAN',
                custom: {
                  mobile: '1.2rem',
                  desktop: '1.4rem',
                },
              },
            },
            next: {
              level: 'SPAN',
              font: 'INTER',
              color: 'GREY_LIGHT',
              text: this.getText(pageType, 'suivant'),
              align: {
                mobile: 'LEFT',
                desktop: 'LEFT',
              },
              size: {
                mobile: 'SPAN',
                desktop: 'SPAN',
                custom: {
                  mobile: '1.2rem',
                  desktop: '1.4rem',
                },
              },
            },
          });
        })
      )
      .subscribe();
  }

  onNavigate(url: string): void {
    void this.#router.navigateByUrl(url);
  }

  getText(pageType: keyof typeof PageType, next: string): string {
    const pagePrefixes: { [key: string]: string } = {
      article: 'Article',
      webserie_details: 'Épisode',
    };

    const prefix = pagePrefixes[pageType] || 'Projet';
    return `${prefix} ${next}`;
  }
}
